import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  clientId?: string; // For client-based tenants
}

interface TenantContextType {
  tenant: Tenant | null;
  setTenant: (tenant: Tenant | null) => void;
  currentTenantId: string | null;
  setCurrentTenantId: (id: string | null) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Mock tenant names for known IDs
const mockTenantNames: Record<string, string> = {
  "34c13c76-204d-43d5-90e3-56a50cfc4e84": "Clover Home",
  "383d7ced-61dd-4a20-88cf-7316bd6b6d66": "Lakewood Villa",
  // Add more mappings as needed
};

const getLogoForTenantId = (_id: string, _name: string): string => {
  return "/assets/JSNU-logo.png";
};

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);

  // Load tenant from localStorage or URL on initial load
  useEffect(() => {
    // Get tenant from subdomain or localStorage
    const subdomain = window.location.hostname.split('.')[0];
    if (subdomain && subdomain !== 'localhost' && subdomain !== 'www') {
      // In a real app, you would fetch tenant details based on subdomain
      const tenantName = mockTenantNames[subdomain] || `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} Healthcare`;
      const mockTenant: Tenant = {
        id: subdomain,
        name: tenantName,
        logoUrl: getLogoForTenantId(subdomain, tenantName),
        clientId: 'client-1', // Default client ID for demo
      };
      setTenant(mockTenant);
      setCurrentTenantId(subdomain);
    } else {
      // Check for tenant in localStorage (for demo purposes)
      const savedTenantId = localStorage.getItem('currentTenantId');

      // Also check logged in user for facility/tenant ID
      const loggedInUserStr = localStorage.getItem('user');
      const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;
      const userFacility = Array.isArray(loggedInUser?.facility) 
        ? (loggedInUser.facility.find((f: any) => f.isPrimary === true) || loggedInUser.facility[0]) 
        : (loggedInUser?.facility || null);
      const userFacilityId = userFacility?.id || loggedInUser?.facilityId || loggedInUser?.tenantId;

      const effectiveTenantId = savedTenantId || (userFacilityId ? String(userFacilityId) : null);

      if (effectiveTenantId) {
        const tenantName = userFacility?.name || mockTenantNames[effectiveTenantId] || `${effectiveTenantId.charAt(0).toUpperCase() + effectiveTenantId.slice(1)} Healthcare`;
        const mockTenant: Tenant = {
          id: effectiveTenantId,
          name: tenantName,
          logoUrl: userFacility?.logoUrl || getLogoForTenantId(effectiveTenantId, tenantName),
          clientId: 'client-1', // Default client ID for demo
        };
        setTenant(mockTenant);
        setCurrentTenantId(effectiveTenantId);
      }
    }
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, setTenant, currentTenantId, setCurrentTenantId }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};